import { setFooterShowing } from "@/lib/ui/footerShowing";
import { type FooterReveal, findFooterReveal } from "../footerReveal";
import { WORDMARK_STATE_ATTRIBUTE, partSelector } from "./parts";
import {
    type Ball,
    type Channel,
    type Court,
    type Spring,
    TUNING,
    columnAt,
    createChannel,
    isBallGone,
    retireOldestPastLimit,
    rollTilt,
    settleChannel,
    stepBall,
    stepChannel,
    throwBall,
    waveAmount,
} from "./physics";

type Letter = {
    element: HTMLElement;
    lift: Channel;
    tilt: Channel;
    scaleX: Channel;
    scaleY: Channel;
    channels: Channel[];
    restTop: number;
    center: number;
    width: number;
    hoverTilt: number;
    lastTransform: string;
};

type FlyingBall = { ball: Ball; element: HTMLElement };

const noop = () => {};

function aim(channel: Channel, target: number, spring: Spring): void {
    channel.target = target;
    channel.stiffness = spring.stiffness;
    channel.damping = spring.damping;
}

function stretch(letter: Letter, amount: number): void {
    letter.scaleY.velocity += amount;
    letter.scaleX.velocity -= amount * TUNING.fetch.stretchWidthRatio;
}

function createLetter(element: HTMLElement): Letter {
    const { restPx, restDeg, restScale } = TUNING.engine;
    const { spring } = TUNING.hover;
    const lift = createChannel(0, restPx, spring);
    const tilt = createChannel(0, restDeg, spring);
    const scaleX = createChannel(1, restScale, spring);
    const scaleY = createChannel(1, restScale, spring);
    return {
        element,
        lift,
        tilt,
        scaleX,
        scaleY,
        channels: [lift, tilt, scaleX, scaleY],
        restTop: 0,
        center: 0,
        width: 0,
        hoverTilt: rollTilt(Math.random),
        lastTransform: "",
    };
}

function letterTransform(letter: Letter): string {
    const atRest =
        letter.lift.value === 0 &&
        letter.tilt.value === 0 &&
        letter.scaleX.value === 1 &&
        letter.scaleY.value === 1;
    if (atRest) return "";
    return `translate3d(0, ${letter.lift.value.toFixed(2)}px, 0) rotate(${letter.tilt.value.toFixed(2)}deg) scale(${letter.scaleX.value.toFixed(4)}, ${letter.scaleY.value.toFixed(4)})`;
}

function ballTransform(ball: Ball): string {
    const half = ball.size / 2;
    return `translate3d(${(ball.x - half).toFixed(1)}px, ${(ball.y - half).toFixed(1)}px, 0) rotate(${ball.angle.toFixed(1)}deg)`;
}

function observeUncovered(
    target: Element,
    lineFromBottom: string,
    onChange: (uncovered: boolean) => void,
): IntersectionObserver {
    const observer = new IntersectionObserver(
        (entries) => {
            const entry = entries.at(-1);
            if (!entry) return;
            const scrolledPast = entry.boundingClientRect.top < 0;
            onChange(entry.isIntersecting || scrolledPast);
        },
        { rootMargin: `0px 0px -${lineFromBottom} 0px` },
    );
    observer.observe(target);
    return observer;
}

function observeReveal(
    stage: HTMLElement,
    footerReveal: FooterReveal | null,
    onChange: (uncovered: boolean) => void,
): { refresh: () => void; disconnect: () => void } {
    let observer: IntersectionObserver | null = null;
    let revealLineFromBottom = -1;

    function measureRevealLine(): number {
        const { bottom, height } = stage.getBoundingClientRect();
        const belowStage = footerReveal
            ? footerReveal.wrapper.getBoundingClientRect().bottom - bottom
            : 0;
        const shownToEnter = height * TUNING.entrance.visibleRatio;
        return Math.max(0, Math.round(belowStage + shownToEnter));
    }

    function refresh(): void {
        const next = measureRevealLine();
        if (next === revealLineFromBottom) return;
        revealLineFromBottom = next;
        observer?.disconnect();
        observer = observeUncovered(
            footerReveal?.edge ?? stage,
            `${revealLineFromBottom}px`,
            onChange,
        );
    }

    refresh();
    return { refresh, disconnect: () => observer?.disconnect() };
}

export function mountFooterWordmark(stage: HTMLDivElement | null): () => void {
    if (!stage) return noop;
    if (
        typeof IntersectionObserver !== "function" ||
        typeof ResizeObserver !== "function"
    ) {
        return noop;
    }
    const layer = stage.querySelector<HTMLElement>(partSelector("balls"));
    const prototype = stage.querySelector<HTMLElement>(
        partSelector("ballPrototype"),
    );
    const hit = stage.querySelector<HTMLElement>(partSelector("hit"));
    if (!layer || !prototype || !hit) return noop;

    const letters = [
        ...stage.querySelectorAll<HTMLElement>(partSelector("letter")),
    ].map(createLetter);
    if (letters.length === 0) return noop;

    return runStage({
        stage,
        hit,
        letters,
        balls: createBallLayer(layer, prototype),
    });
}

function createBallLayer(layer: HTMLElement, prototype: HTMLElement) {
    const flying: FlyingBall[] = [];

    function add(ball: Ball): void {
        const element = prototype.cloneNode(true) as HTMLElement;
        element.hidden = false;
        element.removeAttribute("data-part");
        element.style.width = `${ball.size}px`;
        element.style.height = `${ball.size}px`;
        layer.append(element);
        flying.push({ ball, element });
        retireOldestPastLimit(flying.map((each) => each.ball));
    }

    function step(
        dt: number,
        court: Court,
        onLanding: (column: number, speed: number) => void,
    ): boolean {
        for (let index = flying.length - 1; index >= 0; index--) {
            const each = flying[index];
            if (!each) continue;
            const landing = stepBall(each.ball, dt, court, Math.random);
            if (landing) onLanding(landing.column, landing.speed);
            if (isBallGone(each.ball, court)) {
                each.element.remove();
                flying.splice(index, 1);
                continue;
            }
            each.element.style.transform = ballTransform(each.ball);
        }
        return flying.length > 0;
    }

    function clear(): void {
        for (const { element } of flying) element.remove();
        flying.length = 0;
    }

    return { add, step, clear };
}

function runStage({
    stage,
    hit,
    letters,
    balls,
}: {
    stage: HTMLElement;
    hit: HTMLElement;
    letters: Letter[];
    balls: ReturnType<typeof createBallLayer>;
}): () => void {
    const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");
    const stageId = Symbol("footer-wordmark-stage");
    const listeners = new AbortController();
    const columnEnds: number[] = [];
    const tops: number[] = [];
    const court: Court = {
        width: 0,
        height: 0,
        baselineHeight: 0,
        columnEnds,
        tops,
    };
    let stageLeft = 0;
    let hot: Letter | null = null;
    const footerReveal = findFooterReveal(stage);
    const underSlidingContent = footerReveal !== null;
    let waiting = false;
    let hasBeenUncovered = false;
    let lastFrame = 0;
    let frameId = 0;

    function measure(): void {
        const rect = stage.getBoundingClientRect();
        stageLeft = rect.left;
        court.width = rect.width;
        court.height = rect.height;
        court.baselineHeight = Infinity;
        letters.forEach((letter, index) => {
            const { offsetLeft, offsetTop, offsetWidth, offsetHeight } =
                letter.element;
            const next = letters[index + 1]?.element;
            letter.restTop = offsetTop;
            tops[index] = offsetTop + letter.lift.value;
            letter.width = offsetWidth;
            letter.center = offsetLeft + offsetWidth / 2;
            columnEnds[index] = next
                ? (offsetLeft + offsetWidth + next.offsetLeft) / 2
                : rect.width;
            court.baselineHeight = Math.min(
                court.baselineHeight,
                offsetTop + offsetHeight,
            );
        });
    }

    function wake(): void {
        if (frameId !== 0) return;
        lastFrame = performance.now();
        frameId = requestAnimationFrame(frame);
    }

    function frame(now: number): void {
        const { substepSec, maxFrameSec } = TUNING.engine;
        const dt = Math.max(0, Math.min(maxFrameSec, (now - lastFrame) / 1000));
        lastFrame = now;
        let live = false;

        letters.forEach((letter, index) => {
            for (let t = 0; t < dt; t += substepSec) {
                for (const channel of letter.channels) {
                    stepChannel(channel, substepSec);
                }
            }
            for (const channel of letter.channels) {
                if (settleChannel(channel)) live = true;
            }
            tops[index] = letter.restTop + letter.lift.value;
            const transform = letterTransform(letter);
            if (transform !== letter.lastTransform) {
                letter.element.style.transform = transform;
                letter.lastTransform = transform;
            }
        });

        if (balls.step(dt, court, land)) live = true;

        frameId = live ? requestAnimationFrame(frame) : 0;
    }

    function land(column: number, speed: number): void {
        const letter = letters[column];
        if (!letter) return;
        const { landingLiftShare, landingSquashShare } = TUNING.fetch;
        letter.lift.velocity += speed * landingLiftShare;
        stretch(letter, (-speed / court.baselineHeight) * landingSquashShare);
    }

    function setHot(next: Letter | null): void {
        if (next === hot) return;
        hot?.element.removeAttribute(WORDMARK_STATE_ATTRIBUTE.hot);
        next?.element.setAttribute(WORDMARK_STATE_ATTRIBUTE.hot, "");
        if (next) next.hoverTilt = rollTilt(Math.random);
        hot = next;
    }

    function letterUnder(event: MouseEvent) {
        const x = event.clientX - stageLeft;
        const column = columnAt(x, columnEnds);
        return { letter: letters[column], column, x };
    }

    function hover(event: PointerEvent): void {
        if (event.pointerType === "touch" || waiting) return;
        const { letter, x } = letterUnder(event);
        setHot(letter ?? null);
        if (reducedMotion.matches) return;
        const lift = -court.baselineHeight * TUNING.hover.liftBaselineHeights;
        for (const each of letters) {
            const near = waveAmount(x, each.center, each.width);
            aim(each.lift, lift * near, TUNING.hover.spring);
            aim(each.tilt, each.hoverTilt * near, TUNING.hover.spring);
        }
        wake();
    }

    function leave(): void {
        setHot(null);
        for (const letter of letters) {
            aim(letter.lift, 0, TUNING.hover.settle);
            aim(letter.tilt, 0, TUNING.hover.settle);
        }
        wake();
    }

    function throwFrom(event: MouseEvent): void {
        if (reducedMotion.matches || waiting) return;
        const { letter, column } = letterUnder(event);
        if (!letter || !(court.baselineHeight > 0)) return;
        balls.add(throwBall(column, letter.center, court, Math.random));
        letter.lift.velocity -=
            TUNING.fetch.recoilBaselineHeightsPerSec * court.baselineHeight;
        stretch(letter, TUNING.fetch.recoilStretchPerSec);
        wake();
    }

    function setWaiting(next: boolean): void {
        waiting = next;
        stage.toggleAttribute(WORDMARK_STATE_ATTRIBUTE.waiting, next);
    }

    function hidesUntilNextReveal(): boolean {
        if (underSlidingContent) return true;
        if (hasBeenUncovered) return false;
        return stage.getBoundingClientRect().top >= window.innerHeight;
    }

    function onReveal(uncovered: boolean): void {
        if (uncovered) {
            setWaiting(false);
            hasBeenUncovered = true;
        } else {
            leave();
            reveal.refresh();
            if (hidesUntilNextReveal()) setWaiting(true);
        }
    }

    measure();
    const { signal } = listeners;
    hit.addEventListener("pointerenter", measure, { signal });
    hit.addEventListener("pointermove", hover, { signal });
    hit.addEventListener("pointerleave", leave, { signal });
    hit.addEventListener("pointercancel", leave, { signal });
    hit.addEventListener("click", throwFrom, { signal });

    const reveal = observeReveal(stage, footerReveal, onReveal);
    const footerTop = footerReveal?.edge ?? stage.closest("footer") ?? stage;
    const footerShowing = observeUncovered(
        footerTop,
        TUNING.entrance.floatingChromeZone,
        (uncovered) => setFooterShowing(stageId, uncovered),
    );
    const resize = new ResizeObserver(() => {
        measure();
        reveal.refresh();
    });
    resize.observe(stage);

    return () => {
        listeners.abort();
        resize.disconnect();
        reveal.disconnect();
        footerShowing.disconnect();
        cancelAnimationFrame(frameId);
        setFooterShowing(stageId, false);
        balls.clear();
        stage.removeAttribute(WORDMARK_STATE_ATTRIBUTE.waiting);
        for (const letter of letters) {
            letter.element.style.transform = "";
            letter.element.removeAttribute(WORDMARK_STATE_ATTRIBUTE.hot);
        }
    };
}
